/**
 * Архив доски. Макет data/Задачник.dc.html, isArchiveView.
 *
 * §0.3 и §15.1: архив — отдельное состояние и отдельный экран, а НЕ последний
 * столбец канбана и не удаление. Данные, связи и история сохраняются.
 *
 * §15.4 требует показывать исходную доску и столбец: без них непонятно,
 * куда задача вернётся при восстановлении.
 */

import { Permission } from "@taskflow/contracts";
import { Undo2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { usePermission, type WsError } from "@taskflow/client-core";
import { formatDate } from "@/shared/lib/date";
import {
  AvatarGroup,
  Button,
  type Column,
  ConfirmDialog,
  DataTable,
  type SortState,
  toast,
} from "@/shared/ui";
import { ArrowLeft } from "@/shared/ui/motionIcons";

import { usePrioritiesQuery, useTaskTypesQuery } from "@taskflow/client-core/admin";
import {
  type ArchivedTask,
  useArchivedTasksQuery,
  useRestoreTaskMutation,
} from "@taskflow/client-core/tasks";
import { TaskFilterBar } from "../tasks/TaskFilterBar";
import { TaskPanel } from "../tasks/TaskPanel";
import { useTaskFilters } from "../tasks/useTaskFilters";
import {
  useBoardMembersQuery,
  useBoardQuery,
  useBoardTagsQuery,
} from "@taskflow/client-core/boards";
import { isArchiveSortField, sortArchivedTasks } from "./archiveSort";

export function ArchivePage() {
  const { boardId = "" } = useParams<{ boardId: string }>();
  const board = useBoardQuery(boardId, { skip: boardId === "" });

  // Карточка архивной задачи открывается поверх архива. Ключ — в адресе,
  // а не в состоянии: ссылку на неё можно скопировать и переслать.
  const [searchParams, setSearchParams] = useSearchParams();
  const openKey = searchParams.get("task");
  const openTask = (key: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("task", key);
      return next;
    });
  };
  const closeTask = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("task");
      return next;
    });
  };

  // Фильтры — те же, что на доске, и так же живут в адресе. Сервер
  // применяет их к архивной выборке тем же кодом (tasks/reads.py).
  const { filters, validation, activeCount, setFilter, setFilters, reset } = useTaskFilters();
  const [searchInput, setSearchInput] = useState(filters.search);

  const members = useBoardMembersQuery(boardId, { skip: boardId === "" });
  const boardTags = useBoardTagsQuery(boardId, { skip: boardId === "" });
  const priorities = usePrioritiesQuery({});
  const taskTypes = useTaskTypesQuery({});

  const archived = useArchivedTasksQuery(
    {
      boardId,
      ...(filters.search === "" ? {} : { search: filters.search }),
      ...(filters.assigneeIds.length === 0 ? {} : { assigneeIds: filters.assigneeIds }),
      ...(filters.priorityIds.length === 0 ? {} : { priorityIds: filters.priorityIds }),
      ...(filters.typeIds.length === 0 ? {} : { typeIds: filters.typeIds }),
      ...(filters.tagIds.length === 0 ? {} : { tagIds: filters.tagIds }),
      ...(filters.dueFrom === null || validation.invalidRange ? {} : { dueFrom: filters.dueFrom }),
      ...(filters.dueTo === null || validation.invalidRange ? {} : { dueTo: filters.dueTo }),
      ...(filters.mineOnly ? { mineOnly: true } : {}),
    },
    { skip: boardId === "" || validation.invalidRange },
  );

  // Сортировка, как в списке задач подразделения, — в адресе: пересланная
  // ссылка открывает архив в том же порядке.
  const sortParam = searchParams.get("sort");
  const sortField = isArchiveSortField(sortParam) ? sortParam : null;
  const sortDirection = searchParams.get("dir") === "desc" ? "desc" : "asc";
  const changeSort = (next: SortState) =>
    setSearchParams(
      (prev) => {
        const updated = new URLSearchParams(prev);
        updated.set("sort", next.field);
        updated.set("dir", next.direction);
        return updated;
      },
      { replace: true },
    );

  const priorityRank = useMemo(
    () =>
      new Map(
        (priorities.data?.data ?? []).map((item, index) => [item.name, item.position ?? index]),
      ),
    [priorities.data],
  );
  const rows = useMemo(
    () =>
      sortArchivedTasks(
        archived.data?.data ?? [],
        sortField === null ? null : { field: sortField, direction: sortDirection },
        priorityRank,
      ),
    [archived.data, sortField, sortDirection, priorityRank],
  );

  const [restore, { isLoading: restoring }] = useRestoreTaskMutation();
  const [confirmTask, setConfirmTask] = useState<ArchivedTask | null>(null);

  const canRestore = usePermission(Permission.TASK_RESTORE, board.data?.permissions ?? []);

  const confirmRestore = async () => {
    if (confirmTask === null) return;
    try {
      await restore({ key: confirmTask.key }).unwrap();
      toast.success(
        `Задача ${confirmTask.key} восстановлена`,
        `Возвращена в столбец «${confirmTask.sourceColumnName}»`,
      );
      setConfirmTask(null);
    } catch (raw) {
      toast.error(raw as WsError);
    }
  };

  // Колонки — по макету: «Последняя доска» и «Последний столбец» отдельно
  // (§15.4, AC-21). Причины в таблице макета нет — она в подсказке названия,
  // чтобы не потеряться.
  const columns: Column<ArchivedTask>[] = [
    {
      key: "key",
      sortable: true,
      header: "№",
      width: "90px",
      render: (task) => (
        <span className="font-mono text-[12px] font-semibold text-faint">{task.key}</span>
      ),
    },
    {
      key: "title",
      sortable: true,
      header: "Название",
      render: (task) => (
        // Кнопка, а не только щелчок по строке: открыть карточку должно быть
        // можно и с клавиатуры.
        <button
          type="button"
          className="text-left font-semibold text-fg hover:text-accent hover:underline"
          title={task.reason === null ? undefined : `Причина: ${task.reason}`}
          onClick={(event) => {
            event.stopPropagation();
            openTask(task.key);
          }}
        >
          {task.title}
        </button>
      ),
    },
    {
      key: "type",
      sortable: true,
      header: "Тип",
      width: "110px",
      render: (task) => <span className="text-[13px] text-muted">{task.typeName ?? "—"}</span>,
    },
    {
      key: "priority",
      sortable: true,
      header: "Приоритет",
      width: "110px",
      render: (task) => (
        <span className="text-[13px] text-muted">{task.priorityName ?? "—"}</span>
      ),
    },
    {
      key: "assignees",
      sortable: true,
      header: "Исполнитель",
      width: "110px",
      render: (task) =>
        task.assignees.length === 0 ? (
          <span className="text-faint">—</span>
        ) : (
          <AvatarGroup people={task.assignees} size="xs" max={3} />
        ),
    },
    {
      key: "due",
      sortable: true,
      header: "Срок",
      width: "100px",
      render: (task) => (
        <span className="text-[13px] text-muted tabular-nums">{task.dueDate === null ? "—" : formatDate(task.dueDate)}</span>
      ),
    },
    {
      key: "lastBoard",
      sortable: true,
      header: "Последняя доска",
      width: "160px",
      render: (task) => <span className="text-[13px] text-muted">{task.sourceBoardName}</span>,
    },
    {
      key: "lastColumn",
      sortable: true,
      header: "Последний столбец",
      width: "150px",
      render: (task) => <span className="text-[13px] text-muted">{task.sourceColumnName}</span>,
    },
    {
      key: "archivedAt",
      sortable: true,
      header: "Дата архивации",
      width: "140px",
      render: (task) => (
        <span className="text-[13px] text-fg tabular-nums">{task.archivedAtLabel}</span>
      ),
    },
    {
      key: "archivedBy",
      sortable: true,
      header: "Архивировал",
      width: "150px",
      render: (task) => <span className="text-[13px] text-muted">{task.archivedByName}</span>,
    },
    {
      key: "actions",
      header: "",
      width: "150px",
      align: "right",
      render: (task) =>
        canRestore ? (
          <Button
            size="sm"
            variant="secondary"
            icon={Undo2}
            disabled={!task.canRestore}
            title={
              task.canRestore ? undefined : "Исходный столбец удалён — выберите другой при восстановлении"
            }
            onClick={(event) => {
              event.stopPropagation();
              setConfirmTask(task);
            }}
          >
            Восстановить
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="flex flex-col">
      <header className="flex shrink-0 items-center justify-between gap-4 border-b border-line bg-surface px-4 py-2.5">
        <div className="min-w-0">
          <h1 className="truncate text-[15px] font-extrabold text-fg">
            Архив: {board.data?.name ?? "…"}
          </h1>
          <p className="text-[11px] text-faint">
            Задачи сохраняют данные, связи и историю
          </p>
        </div>
        <Link to={`/boards/${boardId}`}>
          <Button variant="secondary" size="sm" icon={ArrowLeft}>
            Вернуться к доске
          </Button>
        </Link>
      </header>

      <TaskFilterBar
        filters={filters}
        validation={validation}
        activeCount={activeCount}
        setFilter={setFilter}
        setFilters={setFilters}
        reset={reset}
        searchInput={searchInput}
        onSearchInputChange={setSearchInput}
        searchLabel="Поиск по архиву"
        showOverdue={false}
        assignees={(members.data?.data ?? []).map((m) => ({
          id: m.userId,
          label: `${m.lastName} ${m.firstName}`,
        }))}
        priorities={(priorities.data?.data ?? []).map((item) => ({
          id: item.id,
          label: item.name,
        }))}
        types={(taskTypes.data?.data ?? []).map((item) => ({ id: item.id, label: item.name }))}
        tags={(boardTags.data?.data ?? [])
          .filter((tag) => tag.kind === "REGULAR")
          .map((tag) => ({ id: tag.id, label: tag.name }))}
      />

      <DataTable
        caption="Архивированные задачи доски"
        columns={columns}
        rows={rows}
        sort={sortField === null ? null : { field: sortField, direction: sortDirection }}
        onSortChange={changeSort}
        rowKey={(task) => task.key}
        loading={archived.isLoading}
        error={
          archived.error === undefined
            ? null
            : {
                message: (archived.error as WsError).data.message,
                correlationId: (archived.error as WsError).data.correlationId,
              }
        }
        onRetry={() => void archived.refetch()}
        onRowClick={(task) => openTask(task.key)}
        // §15.4: два разных пустых состояния, а не одно общее.
        empty={
          activeCount === 0
            ? {
                title: "В архиве этой доски пока нет задач",
                description: "Архивированные задачи будут отображаться здесь.",
              }
            : {
                title: "По выбранным условиям задачи не найдены",
                description: "Измените условия или сбросьте фильтры.",
              }
        }
      />

      {openKey !== null && <TaskPanel taskKey={openKey} onClose={closeTask} />}

      <ConfirmDialog
        open={confirmTask !== null}
        title="Восстановить задачу"
        description={
          confirmTask === null
            ? ""
            : `Задача ${confirmTask.key} вернётся в столбец «${confirmTask.sourceColumnName}» доски «${confirmTask.sourceBoardName}».`
        }
        confirmLabel="Восстановить"
        loading={restoring}
        onConfirm={() => void confirmRestore()}
        onCancel={() => setConfirmTask(null)}
      />
    </div>
  );
}
